'use client'

import { cn } from '@/utilities/ui'
import React, { useEffect, useRef } from 'react'

import type { Props as MediaProps } from '../types'

import { getMediaUrl } from '@/utilities/getMediaUrl'

export const VideoMedia: React.FC<MediaProps> = (props) => {
  const { onClick, resource, videoClassName, poster, videoUrl } = props

  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const tryPlay = () => {
      const result = video.play()
      if (result && typeof result.then === 'function') {
        result.catch(() => {
          // iOS Low Power Mode, Android Data Saver, or strict autoplay policy.
          // Poster stays visible — no further action needed.
        })
      }
    }

    if (video.readyState >= 2) {
      tryPlay()
    } else {
      video.addEventListener('loadeddata', tryPlay, { once: true })
      return () => video.removeEventListener('loadeddata', tryPlay)
    }
  }, [])

  // Get video source URL
  const getVideoSrc = () => {
    if (videoUrl) {
      return videoUrl
    }
    if (resource && typeof resource === 'object') {
      // Prefer resource.url (absolute Blob URL for videos collection, relative for legacy media)
      if (resource.url) {
        return getMediaUrl(resource.url)
      }
      if (resource.filename) {
        return getMediaUrl(`/media/${resource.filename}`)
      }
    }
    return ''
  }

  // Get poster image URL
  const getPosterSrc = () => {
    if (poster && typeof poster === 'object' && poster.url) {
      return poster.url
    }
    if (poster && typeof poster === 'string') {
      return poster
    }
    return undefined
  }

  const videoSrc = getVideoSrc()
  const posterSrc = getPosterSrc()

  if (videoSrc) {
    return (
      <video
        autoPlay
        className={cn(videoClassName)}
        controls={false}
        loop
        muted
        onClick={onClick}
        playsInline
        poster={posterSrc}
        preload="auto"
        ref={videoRef}
      >
        <source src={videoSrc} type="video/mp4" />
      </video>
    )
  }

  return null
}
